import {
  type ProtocolNames,
  criteriaById,
  objectivesById,
  protocolNames,
} from '@/lib/conversation-model';
import type { CriterionTone, ObjectiveAt } from '@/lib/conversation-model';
import type { ObjectiveTone } from '@/lib/session-labels';
import type {
  GlobalRule,
  Guardrail,
  PlayedCriterion,
  SessionDetail,
} from '@matter_extractor/shared';

/**
 * Everything the six layers of §12.3 read and none of them owns.
 *
 * One bundle rather than eight props repeated down four levels, and the shape is
 * the one `protocoles/contracts.ts` already argues for: the surfaces of this
 * screen are CONTROLLED - the page holds the reading, they draw it - so the
 * thing they share has to be named somewhere both sides can see.
 *
 * The three maps are built ONCE per screen. Rebuilt inside a turn they would be
 * rebuilt forty times on every hover, and a hover is what moves the État column.
 */
export interface ConversationSources {
  readonly detail: SessionDetail;
  /** Réglages, read for the NAME and the N a turn does not carry (§8.5). */
  readonly guardrails: readonly Guardrail[];
  readonly rules: readonly GlobalRule[];
  /** The budget per step of §11.2, or null while Réglages has not answered. */
  readonly budgetPerStepEur: number | null;
  /** The FROZEN criteria (§12.3), by id - never the library of today. */
  readonly criteria: ReadonlyMap<string, PlayedCriterion>;
  readonly objectives: ReadonlyMap<string, ObjectiveAt>;
  readonly names: ProtocolNames;
}

export function conversationSources(input: {
  detail: SessionDetail;
  guardrails: readonly Guardrail[];
  rules: readonly GlobalRule[];
  budgetPerStepEur: number | null;
}): ConversationSources {
  const protocol = input.detail.cascade.protocol;
  return {
    detail: input.detail,
    guardrails: input.guardrails,
    rules: input.rules,
    budgetPerStepEur: input.budgetPerStepEur,
    criteria: criteriaById(input.detail),
    objectives: objectivesById(protocol),
    names: protocolNames(protocol),
  };
}

/**
 * The dot of a status, on the register the two other Sessions screens use - by
 * TONE since #112, so a réussi validated at the passable bar dots yellow.
 */
export const STATUS_DOT: Readonly<Record<ObjectiveTone, string>> = {
  succeeded: 'bg-vert',
  passable: 'bg-jaune',
  failed: 'bg-rouge-deep',
  empty: 'border-2 border-gray-300 bg-white',
};

export const STATUS_TEXT: Readonly<Record<ObjectiveTone, string>> = {
  succeeded: 'text-vert-deep',
  passable: 'text-jaune-deep',
  failed: 'text-rouge-deep',
  empty: 'text-gray-500',
};

/**
 * The ink of a criterion's score and mark, by its tone (§12.3, issue #110).
 *
 * The middle tone is the charter's JAUNE-deep and not an orange: the charter has
 * none, and jaune-deep is already what « attention, pas encore » reads as on
 * every other admin screen (the flagged band, the ignored threshold).
 */
export const CRITERION_TONE_TEXT: Readonly<Record<CriterionTone, string>> = {
  met: 'text-vert-deep',
  passable: 'text-jaune-deep',
  short: 'text-rouge-deep',
  unjudged: 'text-gray-500',
};

/**
 * The rule down the left of an « l'un de » group, by the group's tone (#181):
 * the frame says WHERE the group is, the colour says how it stands - and the
 * mention beside its title says it again in words, so the colour is never alone.
 */
export const GROUP_TONE_BORDER: Readonly<Record<CriterionTone, string>> = {
  met: 'border-vert',
  passable: 'border-jaune',
  short: 'border-rouge-deep',
  unjudged: 'border-gray-200',
};

/**
 * The left inset that puts a layer's content under the TEXT of the register.
 *
 * `3.5rem` of hour plus the `1rem` of the grid's gap plus the `1.25rem` the fil
 * pads with: the accordions of a turn read as belonging to the reply above them
 * only when they start where its column starts. Under `sm` the register itself
 * collapses to two columns, so the inset goes with it.
 */
export const LAYER_INSET = 'px-5 sm:pl-23';

/**
 * The hint both scribe layers - Objectifs, Garde-fous et règles - give the
 * FAREWELL turn (issue #163, §12.3): no scribe was called on a goodbye nobody
 * answers (H81). One string, so the two folds cannot say it two ways.
 */
export const FAREWELL_NOT_EVALUATED = 'non évalué';

/**
 * The hint the Garde-fous et règles layer gives the REREAD row (issue #165,
 * §12.3) - the same words as the farewell turn's, for a different reason:
 * the scribe WAS called, but what it said of the guardrails and the rules is
 * ignored (H96). A name of its own so the two reasons stay apart in the code.
 */
export const REREAD_NOT_EVALUATED = FAREWELL_NOT_EVALUATED;
